/**
 * Product Main Section
 * Handles gallery, variants, quantity, add to cart, and accordions
 */

class ProductMain {
  constructor(container) {
    this.container = container;
    this.sectionId = container.dataset.sectionId;
    this.productId = container.dataset.productId;
    this.productUrl = container.dataset.productUrl;

    // Gallery elements
    this.galleryMain = container.querySelector('[data-gallery-main]');
    this.gallerySlides = container.querySelectorAll('.product-gallery-slide');
    this.thumbnails = container.querySelectorAll('.product-gallery-thumbnail');
    this.model3dButton = container.querySelector('[data-3d-button]');

    // Product info elements
    this.priceWrapper = container.querySelector('[data-price-wrapper]');
    this.priceElement = container.querySelector('[data-product-price]');
    this.variantIdInput = container.querySelector('[data-variant-id]');
    this.addToCartBtn = container.querySelector('[data-add-to-cart]');
    this.addToCartText = container.querySelector('[data-add-to-cart-text]');
    this.quantityInput = container.querySelector('[data-quantity-input]');
    this.quantityMinus = container.querySelector('[data-quantity-minus]');
    this.quantityPlus = container.querySelector('[data-quantity-plus]');

    // Variants
    this.variantOptions = container.querySelectorAll('.product-variant-option input');

    // Accordions
    this.accordions = container.querySelectorAll('[data-accordion]');

    // State
    this.currentSlide = 0;
    this.productData = null;

    this.init();
  }

  async init() {
    await this.fetchProductData();
    this.bindGalleryEvents();
    this.bindVariantEvents();
    this.bindQuantityEvents();
    this.bindAddToCartEvents();
    this.bindAccordionEvents();
    this.initLightbox();
  }

  // =========================================================================
  // Fetch Product Data
  // =========================================================================

  async fetchProductData() {
    try {
      const response = await fetch(`${this.productUrl}.js`);
      this.productData = await response.json();
    } catch (error) {
      console.error('Error fetching product data:', error);
    }
  }

  // =========================================================================
  // Gallery
  // =========================================================================

  bindGalleryEvents() {
    // Thumbnail clicks
    this.thumbnails.forEach((thumbnail, index) => {
      thumbnail.addEventListener('click', () => {
        this.goToSlide(index);
      });
    });

    // 3D Model button
    if (this.model3dButton) {
      this.model3dButton.addEventListener('click', () => {
        const mediaId = this.model3dButton.dataset.mediaId;
        const slideIndex = this.findSlideByMediaId(mediaId);
        if (slideIndex !== -1) {
          this.goToSlide(slideIndex);
        }
      });
    }

    // Keyboard navigation
    if (this.galleryMain) {
      this.galleryMain.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') {
          this.prevSlide();
        } else if (e.key === 'ArrowRight') {
          this.nextSlide();
        }
      });
    }
  }

  goToSlide(index) {
    if (index < 0 || index >= this.gallerySlides.length) return;

    // Update slides
    this.gallerySlides.forEach((slide, i) => {
      slide.classList.toggle('is-active', i === index);
    });

    // Update thumbnails
    this.thumbnails.forEach((thumb, i) => {
      thumb.classList.toggle('is-active', i === index);
    });

    this.currentSlide = index;
  }

  nextSlide() {
    const nextIndex = (this.currentSlide + 1) % this.gallerySlides.length;
    this.goToSlide(nextIndex);
  }

  prevSlide() {
    const prevIndex = (this.currentSlide - 1 + this.gallerySlides.length) % this.gallerySlides.length;
    this.goToSlide(prevIndex);
  }

  findSlideByMediaId(mediaId) {
    for (let i = 0; i < this.gallerySlides.length; i++) {
      if (this.gallerySlides[i].dataset.mediaId === mediaId) {
        return i;
      }
    }
    return -1;
  }

  // =========================================================================
  // Variants
  // =========================================================================

  bindVariantEvents() {
    this.variantOptions.forEach(input => {
      input.addEventListener('change', () => {
        this.updateVariantSelection();
        this.updateSelectedVariant();
      });
    });
  }

  updateVariantSelection() {
    // Update visual selection state
    this.variantOptions.forEach(input => {
      const label = input.closest('.product-variant-option');
      if (label) {
        label.classList.toggle('is-selected', input.checked);
      }
    });
  }

  updateSelectedVariant() {
    if (!this.productData) return;

    // Get selected options
    const selectedOptions = [];
    const optionGroups = this.container.querySelectorAll('.product-variant-group');

    optionGroups.forEach(group => {
      const checkedInput = group.querySelector('input:checked');
      if (checkedInput) {
        selectedOptions.push(checkedInput.value);
      }
    });

    // Find matching variant
    const variant = this.productData.variants.find(v => {
      return selectedOptions.every((option, index) => {
        return v[`option${index + 1}`] === option;
      });
    });

    if (variant) {
      this.updateVariantUI(variant);
      this.updateURL(variant);
      this.updateGalleryForVariant(variant);
    }
  }

  updateVariantUI(variant) {
    // Update hidden input
    if (this.variantIdInput) {
      this.variantIdInput.value = variant.id;
    }

    // Update price
    if (this.priceElement) {
      this.priceElement.textContent = this.formatMoney(variant.price);
    }

    // Update compare at price
    const comparePrice = this.priceWrapper?.querySelector('.product-price-compare');
    if (comparePrice) {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        comparePrice.textContent = this.formatMoney(variant.compare_at_price);
        comparePrice.style.display = '';
      } else {
        comparePrice.style.display = 'none';
      }
    }

    // Update omnibus price visibility
    const omnibusPrice = this.priceWrapper?.querySelector('.product-price-omnibus');
    if (omnibusPrice) {
      omnibusPrice.style.display = (variant.compare_at_price && variant.compare_at_price > variant.price) ? '' : 'none';
    }

    // Update add to cart button
    if (this.addToCartBtn && this.addToCartText) {
      if (variant.available) {
        this.addToCartBtn.disabled = false;
        this.addToCartText.textContent = 'Dodaj do koszyka';
      } else {
        this.addToCartBtn.disabled = true;
        this.addToCartText.textContent = 'Niedostępny';
      }
    }
  }

  updateURL(variant) {
    if (!variant) return;
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url.toString());
  }

  updateGalleryForVariant(variant) {
    // If variant has a featured image, show it
    if (variant.featured_media) {
      const slideIndex = this.findSlideByMediaId(variant.featured_media.id);
      if (slideIndex !== -1) {
        this.goToSlide(slideIndex);
      }
    }
  }

  formatMoney(cents) {
    const amount = (cents / 100).toFixed(2).replace('.', ',');
    return `${amount} zł`;
  }

  // =========================================================================
  // Quantity
  // =========================================================================

  bindQuantityEvents() {
    if (this.quantityMinus) {
      this.quantityMinus.addEventListener('click', () => {
        this.changeQuantity(-1);
      });
    }

    if (this.quantityPlus) {
      this.quantityPlus.addEventListener('click', () => {
        this.changeQuantity(1);
      });
    }

    if (this.quantityInput) {
      this.quantityInput.addEventListener('change', () => {
        this.validateQuantity();
      });
    }
  }

  changeQuantity(delta) {
    if (!this.quantityInput) return;

    const currentValue = parseInt(this.quantityInput.value) || 1;
    const newValue = Math.max(1, currentValue + delta);
    this.quantityInput.value = newValue;
  }

  validateQuantity() {
    if (!this.quantityInput) return;

    let value = parseInt(this.quantityInput.value) || 1;
    if (value < 1) value = 1;
    this.quantityInput.value = value;
  }

  // =========================================================================
  // Add to Cart
  // =========================================================================

  bindAddToCartEvents() {
    const form = this.container.querySelector('.product-form');
    if (!form) return;

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      await this.addToCart(form);
    });
  }

  async addToCart(form) {
    if (!this.addToCartBtn || this.addToCartBtn.disabled) return;

    const originalText = this.addToCartText?.textContent;

    // Loading state
    this.addToCartBtn.disabled = true;
    if (this.addToCartText) {
      this.addToCartText.textContent = 'Dodawanie...';
    }

    try {
      const formData = new FormData(form);

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error('Add to cart failed');
      }

      // Success
      if (this.addToCartText) {
        this.addToCartText.textContent = 'Dodano!';
      }

      // Update cart count
      this.updateCartCount();

      // Otwórz minicart drawer
      if (window.minicartInstance) {
        window.minicartInstance.open();
      }

      // Reset button after delay
      setTimeout(() => {
        this.addToCartBtn.disabled = false;
        if (this.addToCartText) {
          this.addToCartText.textContent = originalText;
        }
      }, 2000);

    } catch (error) {
      console.error('Error adding to cart:', error);

      if (this.addToCartText) {
        this.addToCartText.textContent = 'Błąd';
      }

      setTimeout(() => {
        this.addToCartBtn.disabled = false;
        if (this.addToCartText) {
          this.addToCartText.textContent = originalText;
        }
      }, 2000);
    }
  }

  async updateCartCount() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      const countElements = document.querySelectorAll('[data-cart-count]');
      countElements.forEach(el => {
        el.textContent = cart.item_count;
      });
    } catch (error) {
      console.error('Error updating cart count:', error);
    }
  }

  showCartToast() {
    const toast = document.querySelector('[data-cart-toast]');
    if (!toast) return;

    toast.classList.add('show');
    setTimeout(() => {
      toast.classList.remove('show');
    }, 3000);
  }

  // =========================================================================
  // Accordions
  // =========================================================================

  bindAccordionEvents() {
    this.accordions.forEach(accordion => {
      const toggle = accordion.querySelector('[data-accordion-toggle]');
      const content = accordion.querySelector('[data-accordion-content]');

      if (!toggle || !content) return;

      toggle.addEventListener('click', () => {
        const isOpen = toggle.classList.contains('is-open');

        // Toggle current accordion
        toggle.classList.toggle('is-open', !isOpen);
        content.classList.toggle('is-open', !isOpen);
        toggle.setAttribute('aria-expanded', !isOpen);
      });
    });
  }

  // =========================================================================
  // Lightbox (GLightbox)
  // =========================================================================

  initLightbox() {
    if (typeof GLightbox === 'undefined') return;

    this.glightbox = GLightbox({
      selector: '.glightbox',
      touchNavigation: true,
      loop: true,
      closeOnOutsideClick: true,
      zoomable: true,
      draggable: true,
    });
  }
}

// Initialize
document.addEventListener('DOMContentLoaded', () => {
  const containers = document.querySelectorAll('.product-main-section');
  containers.forEach(container => {
    new ProductMain(container);
  });
});

// Reinitialize on section reload (Shopify editor)
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (e) => {
    const container = e.target.querySelector('.product-main-section');
    if (container) {
      new ProductMain(container);
    }
  });
}
